import { useItems } from '@api/generics';
import QueryKeys from '@api/index';
import API_ROUTES from '@api/routes';
import { useTenants } from '@api/tenants';
import { ButtonSwitch } from '@components/Buttons';
import { LoadingButton } from '@mui/lab';
import {
	Autocomplete,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	TextField,
	Typography
} from '@mui/material';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import { noop } from 'lodash';
import React from 'react';
import { IRole } from 'types/authorization';
import { IDialogProps } from 'types/common';
import { ITenantsRolesEdit, TenantsRolesEditOperation } from 'types/users';

const UserEditTenantDialog = ({
	onSubmit,
	onCancel
}: IDialogProps<ITenantsRolesEdit>) => {
	const { invokeToastableAction } = useToast();
	const { data: rolesData } = useItems<IRole>(
		QueryKeys.roles,
		API_ROUTES.v1.roles.all
	);
	const { data: tenantsData } = useTenants();
	const [selectedButton, setSelectedButton] = React.useState(0);

	const formik = useFormik({
		initialValues: {
			tenants: [],
			roles: []
		},
		onSubmit: async (values) => {
			// create mappings from values
			const mappings: ITenantsRolesEdit = {
				mappings: [
					{
						roles: values.roles,
						tenants: values.tenants
					}
				],
				operation:
					selectedButton === 0
						? TenantsRolesEditOperation.ADD
						: TenantsRolesEditOperation.REMOVE
			};

			await invokeToastableAction(
				() => onSubmit(mappings),
				'users.tenants.dialog.save',
				noop
			);
		}
	});

	return (
		<Dialog open maxWidth="sm" disableRestoreFocus>
			<DialogTitle>{intl.tr('users.edit.tenants.dialog.title')}</DialogTitle>
			<DialogContent>
				<Box pt={2} pb={2}>
					<Grid container spacing={2} pb={2}>
						<Grid item xs={12}>
							<Typography>{intl.tr('users.edit.tenants.add.title')}</Typography>
						</Grid>
						<Grid item xs={12}>
							<ButtonSwitch
								labels={[intl.tr('button.add'), intl.tr('button.remove')]}
								onClick={setSelectedButton}
							/>
						</Grid>
						<Grid item xs={6}>
							<Autocomplete
								data-testid="tenants-user-edit-autocomplete"
								multiple
								filterSelectedOptions
								id="tenants"
								options={tenantsData?.tenants || []}
								getOptionLabel={(option) => option.name}
								onChange={(_, v) => {
									formik.setFieldValue(
										'tenants',
										(v || []).map((item) => item)
									);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label={intl.tr('users.tenants.dialog.label')}
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={6}>
							<Autocomplete
								data-testid="roles-user-edit-autocomplete"
								multiple
								filterSelectedOptions
								id="roles"
								options={rolesData?.data || []}
								getOptionLabel={(option) => option.name}
								onChange={(_, v) => {
									formik.setFieldValue(
										'roles',
										(v || []).map((item) => item)
									);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label={intl.tr('users.roles.dialog.label')}
										fullWidth
									/>
								)}
							/>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onCancel}
					data-testid="users-edit-tenants-roles-cancel-button"
				>
					{intl.tr('button.cancel')}
				</Button>
				<LoadingButton
					variant="contained"
					color="primary"
					loading={formik.isSubmitting}
					onClick={() => formik.handleSubmit()}
					data-testid="users-edit-tenants-roles-approve-button"
					disabled={formik.isSubmitting || !formik.dirty}
				>
					{intl.tr('button.save')}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default UserEditTenantDialog;
